import { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';

export default function DealDrawer({ lead, onClose, onUpdate }) {
  const { tenantId } = useTenant();
  const { currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState('matrix'); // 'matrix', 'schedule', 'audit'
  const [isSaving, setIsSaving] = useState(false);

  // --- LOCAL MAP FOR AUDIT TRAIL ---
  const stageMap = {
    newLeads: 'New Leads',
    contacted: 'Contacted / Discovery',
    negotiating: 'Proposal Sent',
    closedWon: 'Deal Desk Review'
  };

  // --- SERVICE MATRIX STATE ---
  const [services, setServices] = useState({
    apex_os: false,
    ops_hub: false,
    eeko_crm: false,
    dirt_to_turnkey: false,
    bespoke_infra: false,
    perf_consulting: false,
    ui_widgets: false,
    decoupled_web: false,
    custom_spa: false,
    platform_deployment: false,
    custom_pos: false
  });

  // --- CUSTOM PRICING STATE (For Bespoke/Custom Services) ---
  const [customPricing, setCustomPricing] = useState({
    dirt_to_turnkey: { oneTime: 0, mrr: 0 },
    bespoke_infra: { oneTime: 0, mrr: 0 },
    perf_consulting: { oneTime: 0, mrr: 0 },
    ui_widgets: { oneTime: 0, mrr: 0 },
    decoupled_web: { oneTime: 0, mrr: 0 },
    custom_spa: { oneTime: 0, mrr: 0 },
    platform_deployment: { oneTime: 0, mrr: 0 },
    custom_pos: { oneTime: 0, mrr: 0 }
  });

  // --- CALENDAR STATE ---
  const [scheduleForm, setScheduleForm] = useState({
    date: '',
    time: '12:00'
  });

  // Hydrate state from lead on mount
  useEffect(() => {
    if (lead?.serviceMatrix) {
      setServices(prev => ({ ...prev, ...lead.serviceMatrix }));
    }
    if (lead?.customPricing) {
      setCustomPricing(prev => ({ ...prev, ...lead.customPricing }));
    }
  }, [lead]);

  // --- PRICING ENGINE ---
  const calculateTotals = () => {
    let mrr = 0;
    let setup = 0;

    // SaaS Fixed Pricing
    if (services.apex_os) mrr += 249;
    if (services.ops_hub) mrr += 149;
    
    // Dynamic Bundling Math
    if (services.eeko_crm) {
      mrr += services.apex_os ? 99 : 249;
    }

    // Bespoke Pricing Aggregation
    Object.keys(customPricing).forEach(key => {
      if (services[key]) {
        mrr += Number(customPricing[key].mrr) || 0;
        setup += Number(customPricing[key].oneTime) || 0;
      }
    });

    return { totalMrr: mrr, totalSetup: setup };
  };

  const totals = calculateTotals();

  // --- HANDLERS ---
  const handleServiceToggle = (key) => {
    setServices(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCustomPriceChange = (key, field, value) => {
    setCustomPricing(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: Number(value) }
    }));
  };

  const saveDealConfiguration = async () => {
    if (!tenantId || !lead?.id) return;
    setIsSaving(true);
    try {
      const leadRef = doc(db, 'clients', tenantId, lead.sourceCollection, lead.id);
      
      const payload = {
        mrr_value: totals.totalMrr,
        mrr: totals.totalMrr,
        calculatedOneTime: totals.totalSetup,
        amountPaid: totals.totalSetup, 
        totalDueToday: totals.totalSetup + totals.totalMrr,
        serviceMatrix: services,
        customPricing: customPricing,
        last_touch: serverTimestamp()
      };

      await updateDoc(leadRef, payload);
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error("Failed to update deal parameters:", error);
      alert("System Error: Failed to sync deal matrix.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!tenantId || !lead?.id) return;
    setIsSaving(true);
    try {
      const [year, month, day] = scheduleForm.date.split('-');
      const [hours, minutes] = scheduleForm.time.split(':');
      const combinedDate = new Date(year, month - 1, day, hours, minutes);

      const leadRef = doc(db, 'clients', tenantId, lead.sourceCollection, lead.id);
      await updateDoc(leadRef, {
        scheduledDate: combinedDate.toISOString(),
        isUnscheduled: false,
        stage: 'contacted',
        status: 'contacted',
        last_touch: serverTimestamp()
      });

      if (onUpdate) onUpdate();
      setActiveTab('matrix');
      alert("Event Scheduled via Google Calendar integration.");
    } catch (error) {
      console.error("Scheduling failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const pushToDealDesk = async () => {
    if (!tenantId || !lead?.id) return;
    if (window.confirm("Push this configured deal to the Founder Deal Desk?")) {
      setIsSaving(true);
      try {
        const leadRef = doc(db, 'clients', tenantId, lead.sourceCollection, lead.id);
        await updateDoc(leadRef, {
          stage: 'closedWon',
          status: 'Deal Desk Review',
          last_touch: serverTimestamp()
        });
        if (onUpdate) onUpdate();
        onClose();
      } catch (error) {
        console.error("Deal Desk push failed:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (!lead) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end bg-slate-900/60 backdrop-blur-sm animate-in fade-in font-sans">
      <div className="w-full max-w-xl bg-slate-50 border-l border-slate-300 shadow-2xl h-full flex flex-col slide-in-from-right duration-300">
        
        {/* DRAWER HEADER */}
        <div className="bg-slate-900 p-6 flex justify-between items-start shrink-0">
          <div>
            <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
              Active Deal Module
            </span>
            <h2 className="text-2xl font-black text-white mt-3 tracking-tight truncate w-80">{lead.company}</h2>
            <p className="text-xs font-bold text-slate-400 mt-1 flex items-center gap-2">
              <i className="fa-regular fa-envelope"></i> {lead.clientEmail || 'No email provided'} • {lead.contact}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-slate-400 hover:text-white transition-colors"
          >
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        {/* VELOCITY ACTION BAR */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex gap-3 shrink-0">
          <button 
            onClick={() => alert("Copied secure Stripe payload to clipboard.")}
            className="flex-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[9px] font-black px-4 py-2 uppercase tracking-widest transition-colors shadow-sm rounded-sm flex items-center justify-center gap-2"
          >
            <i className="fa-brands fa-stripe"></i> Get Link
          </button>
          <button 
            onClick={pushToDealDesk}
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 text-[9px] font-black px-4 py-2 uppercase tracking-widest transition-colors shadow-sm rounded-sm flex items-center justify-center gap-2"
          >
            <i className="fa-solid fa-file-signature"></i> Deal Desk
          </button>
        </div>

        {/* DRAWER NAVIGATION */}
        <div className="flex bg-slate-100 border-b border-slate-200 shrink-0">
          <button 
            onClick={() => setActiveTab('matrix')} 
            className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors ${activeTab === 'matrix' ? 'bg-white border-b-2 border-emerald-500 text-emerald-600' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border-b-2 border-transparent'}`}
          >
            Service Matrix
          </button>
          <button 
            onClick={() => setActiveTab('schedule')} 
            className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-l border-slate-200 ${activeTab === 'schedule' ? 'bg-white border-b-2 border-emerald-500 text-emerald-600' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border-b-2 border-transparent'}`}
          >
            Scheduler
          </button>
          <button 
            onClick={() => setActiveTab('audit')} 
            className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors border-l border-slate-200 ${activeTab === 'audit' ? 'bg-white border-b-2 border-emerald-500 text-emerald-600' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50 border-b-2 border-transparent'}`}
          >
            Audit Trail
          </button>
        </div>

        {/* DRAWER CONTENT AREA */}
        <div className="flex-1 overflow-y-auto bg-slate-50 custom-scrollbar">
          
          {/* TAB 1: SERVICE MATRIX */}
          {activeTab === 'matrix' && (
            <div className="p-6 space-y-6 animate-in fade-in">
              
              {/* Flagship SaaS */}
              <div className="bg-white border border-slate-200 shadow-sm rounded-sm p-5">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">Core Ecosystem Deployments</h3>
                <div className="space-y-3">
                  <label className={`flex items-center justify-between p-3 border rounded-sm cursor-pointer transition-colors ${services.apex_os ? 'bg-emerald-50/50 border-emerald-500' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={services.apex_os} onChange={() => handleServiceToggle('apex_os')} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 rounded-sm border-slate-300" />
                      <div>
                        <span className="text-xs font-black text-slate-900 uppercase tracking-widest block">Apex OS Suite</span>
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Flagship Operating System</span>
                      </div>
                    </div>
                    <span className="text-xs font-black text-emerald-600">$249/mo</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 border rounded-sm cursor-pointer transition-colors ${services.ops_hub ? 'bg-emerald-50/50 border-emerald-500' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={services.ops_hub} onChange={() => handleServiceToggle('ops_hub')} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 rounded-sm border-slate-300" />
                      <div>
                        <span className="text-xs font-black text-slate-900 uppercase tracking-widest block">Operations Hub</span>
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Intranet & Scheduling</span>
                      </div>
                    </div>
                    <span className="text-xs font-black text-emerald-600">$149/mo</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 border rounded-sm cursor-pointer transition-colors ${services.eeko_crm ? 'bg-emerald-50/50 border-emerald-500' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={services.eeko_crm} onChange={() => handleServiceToggle('eeko_crm')} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 rounded-sm border-slate-300" />
                      <div>
                        <span className="text-xs font-black text-slate-900 uppercase tracking-widest block">eeKoSystem CRM</span>
                        <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">A La Carte CRM Deployment</span>
                      </div>
                    </div>
                    <span className="text-xs font-black text-emerald-600">{services.apex_os ? '$99/mo (Bundled)' : '$249/mo'}</span>
                  </label>
                </div>
              </div>

              {/* Bespoke Engineering & Deployments */}
              <div className="bg-white border border-slate-200 shadow-sm rounded-sm p-5">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">Reinhardt SaaS Custom Engineering</h3>
                <div className="space-y-4">
                  
                  {/* Dynamic Pricing Generator */}
                  {[
                    { key: 'dirt_to_turnkey', label: 'Dirt-To-Turnkey Deployments' },
                    { key: 'bespoke_infra', label: 'Bespoke Digital Infrastructure' },
                    { key: 'perf_consulting', label: 'Performance & Sales Consulting' },
                    { key: 'ui_widgets', label: 'Customer UI Components & Widgets' },
                    { key: 'decoupled_web', label: 'De-Coupled Web Design' },
                    { key: 'custom_spa', label: 'Custom Single Page Applications' },
                    { key: 'platform_deployment', label: 'Platform Deployment & Integration' },
                    { key: 'custom_pos', label: 'Custom POS Services' },
                  ].map(service => (
                    <div key={service.key} className={`border rounded-sm transition-colors ${services[service.key] ? 'border-emerald-500 bg-emerald-50/20' : 'border-slate-200 bg-slate-50'}`}>
                      <label className="flex items-center justify-between p-3 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={services[service.key]} onChange={() => handleServiceToggle(service.key)} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 rounded-sm border-slate-300" />
                          <span className="text-xs font-bold text-slate-800 uppercase tracking-widest">{service.label}</span>
                        </div>
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest border border-slate-200 bg-white px-2 py-0.5 rounded-sm">Custom</span>
                      </label>
                      
                      {services[service.key] && (
                        <div className="px-4 pb-4 pt-1 grid grid-cols-2 gap-3 border-t border-emerald-100 mt-1">
                          <div>
                            <label className="block text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">One-Time / Setup Fee</label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                              <input 
                                type="number" 
                                value={customPricing[service.key].oneTime || ''} 
                                onChange={(e) => handleCustomPriceChange(service.key, 'oneTime', e.target.value)} 
                                className="w-full bg-white border border-slate-300 pl-6 pr-2 py-1.5 text-xs font-bold focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner" 
                                placeholder="0.00" 
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">Monthly Recurring (MRR)</label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                              <input 
                                type="number" 
                                value={customPricing[service.key].mrr || ''} 
                                onChange={(e) => handleCustomPriceChange(service.key, 'mrr', e.target.value)} 
                                className="w-full bg-white border border-slate-300 pl-6 pr-2 py-1.5 text-xs font-bold focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner" 
                                placeholder="0.00" 
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                </div>
              </div>
            </div>
          )}

          {/* TAB 2: SCHEDULER */}
          {activeTab === 'schedule' && (
            <div className="p-6 animate-in fade-in">
              <div className="bg-white border border-slate-200 shadow-sm rounded-sm p-6 text-center">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100">
                  <i className="fa-brands fa-google text-2xl"></i>
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-1">Google Workspace Sync</h3>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-6">Schedule directly to the client's inbox</p>
                
                <form onSubmit={handleScheduleSubmit} className="text-left space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-600 uppercase tracking-widest mb-1.5">Date</label>
                      <input 
                        type="date" required value={scheduleForm.date} onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                        className="w-full border border-slate-300 bg-slate-50 p-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-slate-600 uppercase tracking-widest mb-1.5">Time</label>
                      <input 
                        type="time" required value={scheduleForm.time} onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                        className="w-full border border-slate-300 bg-slate-50 p-2.5 text-xs font-bold text-slate-800 focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                      />
                    </div>
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="w-full py-3 bg-blue-600 text-white rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center gap-2 mt-4"
                  >
                    {isSaving ? <i className="fa-solid fa-spinner fa-spin"></i> : <><i className="fa-regular fa-calendar-check"></i> Book Google Meet</>}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* TAB 3: AUDIT TRAIL */}
          {activeTab === 'audit' && (
            <div className="p-6 animate-in fade-in">
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6 pb-4">
                
                <div className="relative pl-6">
                  <span className="absolute -left-[9px] top-1 w-4 h-4 bg-emerald-500 rounded-full border-4 border-slate-50"></span>
                  <p className="text-xs font-black text-slate-900">Module Update Evaluated</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Just Now • System Payload</p>
                </div>
                
                <div className="relative pl-6">
                  <span className="absolute -left-[9px] top-1 w-4 h-4 bg-slate-300 rounded-full border-4 border-slate-50"></span>
                  <p className="text-xs font-black text-slate-900">Moved to {stageMap[lead.stage] || lead.stage}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">{lead.lastTouch} • {currentUser?.email}</p>
                </div>

                <div className="relative pl-6">
                  <span className="absolute -left-[9px] top-1 w-4 h-4 bg-slate-300 rounded-full border-4 border-slate-50"></span>
                  <p className="text-xs font-black text-slate-900">Lead Minted via {lead.sourceType}</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Initial Entry • CRM Gateway</p>
                </div>

              </div>
            </div>
          )}
        </div>
        
        {/* DRAWER FOOTER (Real-time Totals & Save) */}
        <div className="p-6 bg-white border-t border-slate-200 flex justify-between items-center shrink-0">
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Calculated Value</p>
            <div className="flex items-end gap-3">
              <span className="text-2xl font-black text-emerald-600 leading-none tracking-tight">
                ${totals.totalMrr.toLocaleString()}<span className="text-[10px] text-slate-400 uppercase ml-1">MRR</span>
              </span>
              {totals.totalSetup > 0 && (
                <span className="text-sm font-black text-slate-700 leading-none pb-0.5">
                  + ${totals.totalSetup.toLocaleString()}<span className="text-[8px] text-slate-400 uppercase ml-1">Setup</span>
                </span>
              )}
            </div>
          </div>
          <button 
            onClick={saveDealConfiguration}
            disabled={isSaving}
            className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black px-8 py-3.5 rounded-sm uppercase tracking-widest shadow-sm transition-colors flex items-center gap-2"
          >
            {isSaving ? <><i className="fa-solid fa-spinner fa-spin"></i> Syncing</> : <><i className="fa-solid fa-hard-drive"></i> Save Matrix</>}
          </button>
        </div>

      </div>
    </div>
  );
}